/**
 * CameraShake — small wrapper around Phaser.Cameras.Scene2D.Camera.shake
 * that exposes four intensity preset methods (`micro` / `light` / `medium` /
 * `heavy`) for the EffectsLayer to call on combat events.
 *
 * Per the prompt's §3 "Screen-Space & Camera Effects" — tight high-frequency
 * shake makes machine-gun fire feel heavy, while heavier low-frequency
 * shake gives weight to explosions and big hits.
 *
 * Internally we coalesce overlapping shakes: a new request stronger or equal
 * in intensity than the current shake REPLACES it; weaker requests are
 * dropped while a stronger shake is active. This prevents a 30-per-second
 * stream of `light()` calls (every projectile fire) from cancelling out a
 * `heavy()` explosion shake one frame later. The total duration is also
 * lightly throttled so the camera never permanently jitters during a busy
 * firefight.
 */

import Phaser from 'phaser';

import type { ShakeHandle } from './EffectsLayer.js';

// Preset tuning — duration in ms, intensity in normalised camera units.
// Phaser uses intensity as a fraction of the camera's width/height per frame.
const PRESETS = {
  micro:  { duration: 40,  intensity: 0.0012 }, // subtle single-shot feel
  light:  { duration: 80,  intensity: 0.0030 }, // sustained fire recoil
  medium: { duration: 180, intensity: 0.0075 }, // hit / squad death
  heavy:  { duration: 340, intensity: 0.0145 }, // building destroyed / core damaged
} as const;

type ShakeKind = keyof typeof PRESETS;
const RANK: Record<ShakeKind, number> = { micro: 0, light: 1, medium: 2, heavy: 3 };

export class CameraShake implements ShakeHandle {
  // The strongest shake currently active, plus when it expires (ms timeline).
  private activeKind: ShakeKind | null = null;
  private activeUntilMs = 0;
  // Punch-zoom state. When set, render-time consumers can read it to add a
  // brief zoom-in on top of the shake. Kept here because Camera.zoom is the
  // same property the user-facing zoom-input writes to; we restore the prior
  // value when the punch expires.
  private punchActive = false;
  private punchStartMs = 0;
  private punchDurMs = 0;
  private punchFromZoom = 1;
  private punchToZoom = 1;

  constructor(private readonly scene: Phaser.Scene) {}

  micro(): void  { this.trigger('micro');  }
  light(): void  { this.trigger('light');  }
  medium(): void { this.trigger('medium'); }
  heavy(): void  { this.trigger('heavy');  }

  // Big "match-ending" punch. Plays a heavy shake AND a brief zoom-pulse
  // toward the given world position so the camera kicks into the moment.
  // Used for Core destruction; reserve for genuinely climactic events.
  punch(focusX: number, focusY: number, zoomMul: number = 1.18, durMs: number = 700): void {
    this.heavy();
    const cam = this.scene.cameras.main;
    if (cam === undefined) return;
    // Center on the target so the punch frames the explosion. Use pan() for a
    // smooth slide; if a pan is already running Phaser cancels and replaces it.
    cam.pan(focusX, focusY, Math.min(360, durMs * 0.5), 'Sine.easeOut');
    this.punchActive = true;
    this.punchStartMs = performance.now();
    this.punchDurMs = durMs;
    this.punchFromZoom = cam.zoom;
    this.punchToZoom = cam.zoom * zoomMul;
    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
  }

  private readonly onUpdate = (): void => {
    if (!this.punchActive) {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
      return;
    }
    const cam = this.scene.cameras.main;
    if (cam === undefined) return;
    const t = (performance.now() - this.punchStartMs) / this.punchDurMs;
    if (t >= 1) {
      cam.setZoom(this.punchFromZoom);
      this.punchActive = false;
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
      return;
    }
    // Asymmetric envelope: quick attack to peak zoom in the first 25%, then
    // ease back. Reads as a kinetic hit, not a smooth dolly.
    const envelope = t < 0.25 ? t / 0.25 : 1 - (t - 0.25) / 0.75;
    const z = this.punchFromZoom + (this.punchToZoom - this.punchFromZoom) * Math.max(0, envelope);
    cam.setZoom(z);
  };

  private trigger(kind: ShakeKind): void {
    const now = performance.now();
    // If a stronger shake is still in progress, ignore weaker requests so the
    // big shake's perceived intensity isn't muddled.
    if (this.activeKind !== null && now < this.activeUntilMs && RANK[kind] < RANK[this.activeKind]) return;
    const preset = PRESETS[kind];
    const cam = this.scene.cameras.main;
    if (cam === undefined) return;
    cam.shake(preset.duration, preset.intensity, false);
    this.activeKind = kind;
    this.activeUntilMs = now + preset.duration;
  }
}
